import React, { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';

import { ClassName, GADataType, I18nText } from '../../../../../types';
import { IconsEnum } from '../../../../../assets/icons/types';
import { LifestylesSetUUID } from '../../../lifestylesSetsTypes';

import {
  FETCH_LIFESTYLES_SET_DOWNLOAD_URLS_QUERY,
  FetchLifestylesSetDownloadUrlsQueryResponse
} from '../../../queries/fetchLifestylesSetDownloadUrls.query';

import { useCurrentUser } from '../../../../../auth/hooks/useAuth';
import { useLifestylesSet } from '../../../hooks/useLifestylesSet';
import { useShowToastOnErrorChange } from '../../../../../common/hooks/useShowToastOnErrorChange';
import { useToastNotification } from '../../../../../common/hooks/useToastNotification';

import { PureTooltipIconButtonHelper } from '../../../../../helpers/buttons/PureTooltipIconButtonHelper';
import { TooltipPlacement } from '../../../../../helpers/tooltips/tooltipsConstants';

import { downloadFile } from '../../../../../utils/downloadFile';
import { getFileNameFromURL } from '../../../../../utils/getFileNameFromURL';

import { LifestylesSetCache } from '../../../LifestylesSetCache';

import { words } from '../../../../../locales/keys';
import { CommonPermissions } from '../../../../common/commonConstants';

interface DownloadLifestylesSetSeparatelyButtonProps {
  className?: ClassName;
  i18nText?: I18nText;
  icon?: IconsEnum;
  iconClassName?: ClassName;
  lifestylesSetUuid: LifestylesSetUUID;
  tooltipI18nText?: I18nText;
  tooltipPlacement?: TooltipPlacement;
  tooltipSingleton?: boolean;
}

function DownloadLifestylesSetSeparatelyButton({
  className,
  dataGa,
  i18nText,
  icon,
  iconClassName,
  lifestylesSetUuid,
  tooltipI18nText,
  tooltipPlacement,
  tooltipSingleton
}: DownloadLifestylesSetSeparatelyButtonProps & GADataType) {
  const currentUser = useCurrentUser();

  const { lifestylesSetLoading, refetchLifestylesSet, lifestylesSetError } =
    useLifestylesSet<FetchLifestylesSetDownloadUrlsQueryResponse>({
      cacheKey: LifestylesSetCache.downloadUrls(lifestylesSetUuid),
      options: {
        enabled: false,
        enabledPlaceholder: false
      },
      query: FETCH_LIFESTYLES_SET_DOWNLOAD_URLS_QUERY,
      uuid: lifestylesSetUuid
    });

  const { showToastI18nNotification } = useToastNotification({
    appearance: 'success',
    i18nMessage: words.fileDownloadStarted
  });

  useShowToastOnErrorChange({ error: lifestylesSetError });

  const withBlob = currentUser.hasPermissions(
    CommonPermissions.READ_DOWNLOADS_WITH_FETCH
  );

  const handleClick = useCallback<() => Promise<void>>(async () => {
    const refetchLifestylesSetResponse = await refetchLifestylesSet();

    const downloadUrls =
      refetchLifestylesSetResponse?.data?.lifestylesSet?.downloadUrls;

    if (isEmpty(downloadUrls)) {
      return;
    }

    showToastI18nNotification();

    await Promise.all(
      map(downloadUrls, ({ url }) =>
        downloadFile({
          downloadFile: {
            name: getFileNameFromURL(url),
            file: url
          },
          withBlob
        })
      )
    );
  }, [refetchLifestylesSet, showToastI18nNotification, withBlob]);

  return (
    <PureTooltipIconButtonHelper
      className={className}
      dataGa={dataGa}
      disabled={lifestylesSetLoading}
      i18nText={i18nText}
      icon={icon}
      iconClassName={iconClassName}
      onClick={handleClick}
      tooltipI18nText={tooltipI18nText}
      tooltipPlacement={tooltipPlacement}
      tooltipSingleton={tooltipSingleton}
    />
  );
}

export default DownloadLifestylesSetSeparatelyButton;
